import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, Globe, QrCode, Gift, Lock } from "lucide-react";

const LOGO_SRC =
  "https://customer-assets.emergentagent.com/job_sasha-host-flow/artifacts/hb7r80co_Jant%C3%A9%20Travel%20Concierges.png";

const SASHA_SITE = "https://jantetcsasha.com";
const LEGAL = `${SASHA_SITE}/legal`;

const TRAVEL_ITEMS = ["Flights", "Cars", "Activities", "Properties"];

const CONCIERGE_LINKS = [
  { label: "Concierge@JanteTravelConcierges.com", href: "mailto:Concierge@JanteTravelConcierges.com", Icon: Mail },
  { label: "+1 971 460 8756", href: "tel:+19714608756", Icon: Phone },
  { label: "WhatsApp the concierge", href: "https://wa.me/19714608756", Icon: MessageCircle },
  { label: "JanteTravelConcierges.com", href: "https://JanteTravelConcierges.com", Icon: Globe },
  { label: "Scan-to-start QR", href: `${SASHA_SITE}/qr`, Icon: QrCode },
];

const POLICY_COLUMNS = [
  [
    { label: "All Policies", href: LEGAL },
    { label: "Bookings", href: `${LEGAL}#bookings` },
    { label: "Document Control", href: `${LEGAL}#documents` },
  ],
  [
    { label: "SMS & Text Consent", href: `${LEGAL}#sms` },
    { label: "Cancellations", href: `${LEGAL}#cancellations` },
    { label: "Accessibility", href: `${LEGAL}#accessibility` },
  ],
  [
    { label: "Recorded Calls", href: `${LEGAL}#calls` },
    { label: "Privacy", href: `${LEGAL}#privacy` },
  ],
];

const GoldEyebrow = ({ children }) => (
  <div className="inline-block">
    <div className="border-t-2 border-[#B0895C] pt-2 text-[13px] font-semibold tracking-[0.25em] uppercase text-[#B0895C]">
      {children}
    </div>
  </div>
);

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-[#E3DED5] bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14">
        {/* Brand */}
        <div className="md:col-span-4">
          <a href={SASHA_SITE} target="_blank" rel="noreferrer" data-testid="footer-brand-logo">
            <div className="bg-white inline-block p-3 shadow-sm">
              <img src={LOGO_SRC} alt="Janté Travel Concierges" className="h-24 w-auto object-contain" />
            </div>
          </a>
          <p className="mt-6 text-base text-[#3A3A3C] leading-relaxed max-w-sm">
            Anticipatory luxury travel — Janté Travel Concierges, with Sasha,
            your AI travel companion who plans with the heart of a five-star
            concierge.
          </p>
        </div>

        {/* Travel */}
        <div className="md:col-span-3" data-testid="footer-travel-col">
          <GoldEyebrow>Travel</GoldEyebrow>
          <ul className="mt-5 space-y-3">
            {TRAVEL_ITEMS.map((label) => (
              <li
                key={label}
                data-testid={`footer-travel-${label.toLowerCase()}`}
                className="text-lg text-[#2A1F1A]"
              >
                {label}
              </li>
            ))}
          </ul>
        </div>

        {/* Concierge + Refer */}
        <div className="md:col-span-5" data-testid="footer-concierge-col">
          <GoldEyebrow>Concierge</GoldEyebrow>
          <ul className="mt-5 space-y-3">
            {CONCIERGE_LINKS.map(({ label, href, Icon }) => (
              <li key={label}>
                <a
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={`footer-concierge-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex items-center gap-3 text-base lg:text-lg text-[#C03028] hover:text-[#9F2620] transition-colors duration-200"
                >
                  <Icon size={17} strokeWidth={1.75} className="shrink-0" />
                  {label}
                </a>
              </li>
            ))}
          </ul>

          <div className="mt-10" data-testid="footer-refer-block">
            <div className="border-t-2 border-[#B0895C] pt-2 inline-flex items-center gap-2 text-[13px] font-semibold tracking-[0.25em] uppercase text-[#B0895C]">
              <Gift size={14} strokeWidth={1.75} />
              Refer a friend · Earn $100
            </div>
            <p className="mt-3 text-base text-[#3A3A3C] leading-relaxed">
              When a friend you refer books their first Janté itinerary, you
              earn a $100 credit toward your next trip.
            </p>
            <a
              href={`${SASHA_SITE}/register`}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-refer-signup-link"
              className="mt-4 inline-block text-sm font-semibold tracking-[0.2em] uppercase text-[#C03028] hover:text-[#9F2620] transition-colors duration-200"
            >
              Sign up to claim your code →
            </a>
          </div>
        </div>
      </div>

      {/* Policy links */}
      <div className="border-t border-[#E3DED5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-4">
          {POLICY_COLUMNS.map((col, i) => (
            <ul key={i} className="space-y-4">
              {col.map(({ label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`footer-policy-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-sm font-medium tracking-[0.2em] uppercase text-[#C03028] hover:text-[#9F2620] transition-colors duration-200"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[#E3DED5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-xs tracking-[0.15em] uppercase text-[#6E6E70]">
            © 2023–{new Date().getFullYear()} Janté Luxury Travel Inc.
          </span>
          <div className="text-center text-[11px] tracking-[0.18em] uppercase text-[#6E6E70] leading-relaxed">
            A subsidiary of Janté Luxury Travel Inc.
            <br />
            <a
              href="https://JanteLuxHotels.com"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-jante-lux-link"
              className="text-[#B0895C] hover:text-[#C03028] transition-colors duration-200"
            >
              Janté Lux Hotels &amp; Residences
            </a>{" "}
            · Janté Travel Concierges™
          </div>
          <Link
            to="/admin/login"
            data-testid="footer-admin-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#B0895C] hover:text-[#C03028] border border-[#B0895C]/60 hover:border-[#C03028] px-4 py-2 transition-colors duration-200"
          >
            <Lock size={12} strokeWidth={1.75} />
            Administration
          </Link>
        </div>
      </div>
    </footer>
  );
}
